import {
  COMPLIANCE_T1_KEYS,
  isReasonedCadenceKey,
  getComplianceSettingDefinition,
  type ComplianceSettingKey,
  type ComplaintCategorySet,
  type ComplianceSettingScope,
  type NumberSettingDefinition,
} from './registry';
import { validateComplaintCategoryWrite } from '../complaint-categories';
export type ComplianceSettingWarning = 'INVALID_STORED_VALUE' | 'STORED_VALUE_TOO_LOOSE';
const SIRS_REMINDER_LEADS = new Set([
  'sirs_priority_1_t12_reminder_lead',
  'sirs_priority_1_t4_reminder_lead',
  'sirs_priority_1_t2_reminder_lead',
]);
function isLooserValue(key: string, value: number, definition: NumberSettingDefinition): boolean {
  if (SIRS_REMINDER_LEADS.has(key)) return value > definition.platformDefault;
  return isT2ValueLooser(value, definition.platformDefault, definition.tighterDirection!);
}
export type WriteRefusalCode = 'T2_CANNOT_BE_LOOSENED' | 'T1_NOT_CONFIGURABLE' | 'INVALID_VALUE';
type Value = number | ComplaintCategorySet;

/**
 * The deadline library consumes this deliberately narrow, already-resolved
 * view. Keeping validation in this module means a stale or looser persisted
 * T2 value cannot reach a clock through an alternate caller.
 */
export interface ComplianceDeadlineSettings {
  complaint_acknowledgement_target?: number;
  complaint_ageing_target?: number;
  open_disclosure_target?: number;
  risk_review_low_cadence_days?: number;
  risk_review_medium_cadence_days?: number;
  risk_review_high_cadence_days?: number;
  risk_review_extreme_cadence_days?: number;
  policy_review_cadence_days?: number;
  association_review_cadence_days?: number;
  restrictive_practice_chemical_review_cadence_days?: number;
  restrictive_practice_other_review_cadence_days?: number;
  restrictive_practice_review_warning_lead?: number;
  restrictive_practice_emergency_review_reminder_lead?: number;
  risk_review_warning_lead?: number;
  conflict_of_interest_warning_lead?: number;
  governing_body_skills_warning_lead?: number;
  audit_schedule_warning_lead?: number;
  sirs_priority_1_t12_reminder_lead?: number;
  sirs_priority_1_t4_reminder_lead?: number;
  sirs_priority_1_t2_reminder_lead?: number;
  ndb_assessment_reminder_lead?: number;
}

const DEADLINE_SETTING_KEYS = new Set<keyof ComplianceDeadlineSettings>([
  'complaint_acknowledgement_target',
  'complaint_ageing_target',
  'open_disclosure_target',
  'risk_review_low_cadence_days',
  'risk_review_medium_cadence_days',
  'risk_review_high_cadence_days',
  'risk_review_extreme_cadence_days',
  'policy_review_cadence_days',
  'association_review_cadence_days',
  'restrictive_practice_chemical_review_cadence_days',
  'restrictive_practice_other_review_cadence_days',
  'restrictive_practice_review_warning_lead',
  'restrictive_practice_emergency_review_reminder_lead',
  'risk_review_warning_lead',
  'conflict_of_interest_warning_lead',
  'governing_body_skills_warning_lead',
  'audit_schedule_warning_lead',
  'sirs_priority_1_t12_reminder_lead',
  'sirs_priority_1_t4_reminder_lead',
  'sirs_priority_1_t2_reminder_lead',
  'ndb_assessment_reminder_lead',
]);

/** Converts controlled, resolved settings into the only shape clocks accept. */
export function toComplianceDeadlineSettings(
  settings: ReadonlyArray<{ key: ComplianceSettingKey; value: Value }>,
): ComplianceDeadlineSettings {
  const deadlines: ComplianceDeadlineSettings = {};
  for (const setting of settings) {
    if (DEADLINE_SETTING_KEYS.has(setting.key as keyof ComplianceDeadlineSettings)) {
      deadlines[setting.key as keyof ComplianceDeadlineSettings] = setting.value as number;
    }
  }
  return deadlines;
}
export function isT2ValueLooser(
  proposed: number,
  platformDefault: number,
  direction: NonNullable<NumberSettingDefinition['tighterDirection']>,
): boolean {
  return direction === 'LOWER_IS_TIGHTER' ? proposed > platformDefault : proposed < platformDefault;
}
const validNumber = (d: NumberSettingDefinition, v: unknown) =>
  typeof v === 'number' && Number.isInteger(v) && v >= d.minimum && v <= d.maximum ? v : null;
const validCategories = (v: unknown): v is ComplaintCategorySet =>
  validateComplaintCategoryWrite(v).ok;
function read(
  key: ComplianceSettingKey,
  candidate: unknown,
): { value: Value; warning?: ComplianceSettingWarning } {
  const d = getComplianceSettingDefinition(key)!;
  if (d.kind === 'OBJECT')
    return validCategories(candidate)
      ? { value: candidate }
      : { value: d.platformDefault, warning: 'INVALID_STORED_VALUE' };
  if (
    typeof candidate === 'number' &&
    Number.isInteger(candidate) &&
    (d.tier === 'T2' || SIRS_REMINDER_LEADS.has(key)) &&
    !isReasonedCadenceKey(key) &&
    isLooserValue(key, candidate, d)
  )
    return { value: d.platformDefault, warning: 'STORED_VALUE_TOO_LOOSE' };
  const value = validNumber(d, candidate);
  return value === null ? { value: d.platformDefault, warning: 'INVALID_STORED_VALUE' } : { value };
}
/** Prefers valid local configuration and never turns a corrupt historic row into a read failure. */
export function resolveComplianceSetting(
  key: ComplianceSettingKey,
  values: { tenantValue?: unknown; facilityValue?: unknown },
) {
  const d = getComplianceSettingDefinition(key)!;
  const f = values.facilityValue === undefined ? undefined : read(key, values.facilityValue);
  const t = values.tenantValue === undefined ? undefined : read(key, values.tenantValue);
  if (f && !f.warning)
    return {
      value: f.value,
      provenance: 'FACILITY' as const,
      warnings: [] as ComplianceSettingWarning[],
    };
  if (t && !t.warning)
    return {
      value: t.value,
      provenance: 'TENANT' as const,
      warnings: f?.warning ? [f.warning] : [],
    };
  return {
    value: d.platformDefault,
    provenance: 'PLATFORM' as const,
    warnings: [f?.warning, t?.warning].filter(Boolean) as ComplianceSettingWarning[],
  };
}
/** Returns an explicit refusal rather than silently discarding a person's choice. */
export function validateComplianceSettingWrite(
  key: string,
  proposed: unknown,
  scope: ComplianceSettingScope,
  reason?: string,
) {
  if (COMPLIANCE_T1_KEYS.includes(key as (typeof COMPLIANCE_T1_KEYS)[number]))
    return {
      ok: false as const,
      code: 'T1_NOT_CONFIGURABLE' as const,
      message: 'This setting is set by law and cannot be changed.',
    };
  const d = getComplianceSettingDefinition(key);
  if (!d || (scope === 'TENANT_AND_FACILITY' && d.scope !== 'TENANT_AND_FACILITY'))
    return {
      ok: false as const,
      code: 'INVALID_VALUE' as const,
      message: 'This compliance setting is not available.',
    };
  if (d.kind === 'OBJECT')
    return validCategories(proposed)
      ? { ok: true as const, value: proposed }
      : {
          ok: false as const,
          code: 'INVALID_VALUE' as const,
          message: 'Keep stable category codes and include OTHER.',
        };
  if (
    (d.tier === 'T2' || SIRS_REMINDER_LEADS.has(key)) &&
    typeof proposed === 'number' &&
    Number.isInteger(proposed) &&
    isLooserValue(key, proposed, d)
  ) {
    if (isReasonedCadenceKey(key)) {
      if (proposed < d.minimum || proposed > d.maximum) {
        return {
          ok: false as const,
          code: 'INVALID_VALUE' as const,
          message: 'Enter a whole value within the allowed range.',
        };
      }
      if ((reason?.trim().length ?? 0) >= 10) return { ok: true as const, value: proposed };
      return {
        ok: false as const,
        code: 'INVALID_VALUE' as const,
        message: 'Give a reason of at least 10 characters for a longer review cadence.',
      };
    }
    return {
      ok: false as const,
      code: 'T2_CANNOT_BE_LOOSENED' as const,
      message: 'This target can only be made tighter than the platform default.',
    };
  }
  const value = validNumber(d, proposed);
  if (value === null)
    return {
      ok: false as const,
      code: 'INVALID_VALUE' as const,
      message: 'Enter a whole value within the allowed range.',
    };
  return { ok: true as const, value };
}
